import { useEffect, useMemo, useRef, useState } from 'react';
import { XIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';

const CannedMessageSuggestions = ({
  inputText,
  shortcuts,
  onSelect,
  processMessage,
}) => {
  const popupRef = useRef(null);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [manuallyClosed, setManuallyClosed] = useState(false);

  // Find suggestions based on the first word typed
  const suggestions = useMemo(() => {
    if (!inputText?.trim()) {
      return [];
    }

    const words = inputText.trim().split(/\s+/);
    const firstWord = words[0].toLowerCase();

    // Don't show suggestions if the first word is less than 3 characters
    if (firstWord.length < 3) {
      return [];
    }

    // Don't show suggestions if there are multiple words (only for first word)
    if (words.length > 1) {
      return [];
    }

    // Don't show suggestions if the input ends with a space (user is starting a new word)
    if (inputText.endsWith(' ')) {
      return [];
    }

    const matchingSuggestions = shortcuts
      .map((shortcut) => {
        const shortcutLower = shortcut.shortcut.toLowerCase();
        const contentText = processMessage(shortcut.message).toLowerCase();

        // Check if the first word matches either the shortcut or content
        const shortcutMatch =
          shortcutLower.includes(firstWord) ||
          firstWord.includes(shortcutLower);
        const contentMatch =
          contentText.includes(firstWord) || firstWord.includes(contentText);

        if (shortcutMatch || contentMatch) {
          // Calculate match score with better logic for numbers
          let matchScore = 1;

          // Higher score for exact matches
          if (
            shortcutLower === firstWord ||
            contentText.includes(` ${firstWord} `)
          ) {
            matchScore = 3;
          }
          // Higher score for starts with
          else if (
            shortcutLower.startsWith(firstWord) ||
            contentText.startsWith(firstWord)
          ) {
            matchScore = 2;
          }
          // Bonus for shortcut matches (more relevant than content matches)
          else if (shortcutMatch) {
            matchScore = 1.5;
          }

          return {
            ...shortcut,
            matchType: shortcutMatch ? 'shortcut' : 'content',
            matchScore,
            searchTerm: firstWord, // Store for highlighting
          };
        }
        return null;
      })
      .filter(Boolean)
      .sort((a, b) => b.matchScore - a.matchScore)
      .slice(0, 3); // Limit to 3 suggestions

    return matchingSuggestions;
  }, [inputText, shortcuts, processMessage]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setIsSuggestionsOpen(false);
        setManuallyClosed(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsSuggestionsOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle these specific keys when suggestions are visible
      if (!['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key)) {
        return;
      }

      // Only handle events if suggestions are visible
      if (!suggestions || suggestions.length === 0) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      switch (e.key) {
        case 'ArrowDown':
          setFocusedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          setFocusedIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;
        case 'Enter':
          if (focusedIndex >= 0 && focusedIndex < suggestions.length) {
            handleSuggestionSelect(suggestions[focusedIndex]);
          }
          break;
        case 'Escape':
          setIsSuggestionsOpen(false);
          setManuallyClosed(true);
          break;
      }
    };

    // Only add the event listener when suggestions are visible
    if (suggestions && suggestions.length > 0) {
      document.addEventListener('keydown', handleKeyDown, true); // Use capture phase
      return () => {
        document.removeEventListener('keydown', handleKeyDown, true);
      };
    }
  }, [suggestions, focusedIndex, setIsSuggestionsOpen]);

  // Reset focus when suggestions change
  useEffect(() => {
    setFocusedIndex(-1);
  }, [suggestions]);

  const handleSuggestionSelect = (selectedSuggestion) => {
    const words = inputText.trim().split(/\s+/);
    const firstWord = words[0];

    // Replace the first word with the processed message content
    const processedMessage = processMessage(selectedSuggestion.message);
    const newText =
      processedMessage +
      (words.length > 1 ? ' ' + words.slice(1).join(' ') : '') +
      ' ';

    onSelect(newText);
    setIsSuggestionsOpen(false);
  };

  // Escape special regex characters for safe highlighting
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const renderContent = (currentContent, searchTerm) => {
    if (!currentContent?.content) return null;

    return (
      <>
        {currentContent.content.map((node, nodeIndex) => {
          if (node.type === 'paragraph') {
            // Handle empty paragraph
            if (!node.content || node.content.length === 0) {
              return <br key={nodeIndex} />;
            }

            return (
              <div key={nodeIndex}>
                {node.content?.map((item, itemIndex) => {
                  if (item.type === 'text') {
                    return (
                      <span key={itemIndex}>
                        {renderShortcut(item.text, searchTerm)}
                      </span>
                    );
                  } else if (item.type === 'mention') {
                    return (
                      <code
                        key={itemIndex}
                        className='relative rounded-md bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold'
                      >
                        {item.attrs.id}
                      </code>
                    );
                  } else if (item.type === 'hardBreak') {
                    return <br key={itemIndex} />;
                  }
                  return null;
                })}
              </div>
            );
          }
          return null;
        })}
      </>
    );
  };

  const renderShortcut = (text, term) => {
    if (!term || !text.toLowerCase().includes(term.toLowerCase())) {
      return text;
    }

    const escapedTerm = escapeRegex(term);
    const parts = text.split(new RegExp(`(${escapedTerm})`, 'gi'));

    return (
      <>
        {parts.map((part, partIndex) =>
          part.toLowerCase() === term.toLowerCase() ? (
            <strong key={partIndex} className='font-bold text-primary'>
              {part}
            </strong>
          ) : (
            part
          )
        )}
      </>
    );
  };

  // Update isSuggestionsOpen based on whether we have suggestions
  // Only auto-open if it's not manually closed
  useEffect(() => {
    if (suggestions && suggestions.length > 0 && !manuallyClosed) {
      setIsSuggestionsOpen(true);
    } else if (!suggestions || suggestions.length === 0) {
      setIsSuggestionsOpen(false);
      setManuallyClosed(false); // Reset manual close when suggestions disappear
    }
  }, [suggestions, manuallyClosed]);

  // Don't render anything if there are no suggestions or if suggestions are closed
  if (!suggestions || suggestions.length === 0 || !isSuggestionsOpen) {
    return null;
  }

  return (
    <div
      ref={popupRef}
      className='absolute bottom-full z-50 mb-1 max-h-64 w-full rounded-lg border border-border bg-background shadow-2xl'
    >
      <div className='flex items-center justify-between border-b border-border p-2'>
        <h4 className='text-sm font-medium text-foreground'>
          Suggested Canned Messages
        </h4>
        <button
          onClick={() => {
            setIsSuggestionsOpen(false);
            setManuallyClosed(true);
          }}
          className='rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground'
        >
          <XIcon className='h-4 w-4' />
        </button>
      </div>

      <ScrollArea className='max-h-64'>
        <div className='divide-y divide-border'>
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion._id || index}
              className={cn(
                'w-full px-4 py-2 text-left transition-colors hover:bg-muted',
                'focus:bg-muted focus:outline-none focus:ring-1 focus:ring-inset focus:ring-primary',
                focusedIndex === index &&
                  'bg-muted ring-1 ring-inset ring-primary',
                focusedIndex === suggestions.length - 1 && 'rounded-b-lg'
              )}
              onClick={() => handleSuggestionSelect(suggestion)}
              onMouseEnter={() => setFocusedIndex(index)}
            >
              <div className='flex items-start justify-between gap-2'>
                <div className='min-w-0 flex-1'>
                  <div className='mb-1 text-sm text-foreground'>
                    {renderShortcut(
                      suggestion.shortcut,
                      suggestion.searchTerm || inputText.trim().split(/\s+/)[0]
                    )}
                  </div>
                  <div className='line-clamp-2 text-xs text-muted-foreground'>
                    {renderContent(
                      suggestion.message,
                      suggestion.searchTerm || inputText.trim().split(/\s+/)[0]
                    )}
                  </div>
                </div>
                {suggestion.global && (
                  <span className='flex-shrink-0 whitespace-nowrap rounded-full bg-primary/10 px-2 py-1 text-xs text-primary'>
                    Global
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

export default CannedMessageSuggestions;
